import { Box, useTheme, Typography } from "@mui/material";
import { Helmet } from "react-helmet";
import React, { useState } from "react";
import GridContainer from "../../components/grid/GridContainer.jsx";
import GridItem from "../../components/grid/GridItem.jsx";
import { getLeranMoreStyle } from "../../components/customstyles/LearnMoreStyle.jsx";
import AboutAssessmentOne from "../../assets/images/LearnMore/AboutAssessmentOne.png";
import AboutAssessmentTwo from "../../assets/images/LearnMore/AboutAssessmentTwo.png";
import AboutAssessmentThree from "../../assets/images/LearnMore/AboutAssessmentThree.png";
import AboutAssessmentFour from "../../assets/images/LearnMore/AboutAssessmentFour.png";
import AboutAssessmentFive from "../../assets/images/LearnMore/AboutAssessmentFive.png";

import Breadcrumbs from "./Breadcrumbs.jsx";

const useStyles = () => {
  let theme = useTheme();
  return getLeranMoreStyle(theme);
};

function AboutAssessment() {
  const styles = useStyles();
  const [loadingStarted, setLoading] = useState(true);

  const handleBodyAreaLoading = () => {
    setLoading(false);
  };
  return (
    <Box sx={styles.learnMorePageStyle}>
      <Helmet>
        <title>Patients</title>
      </Helmet>
      <Breadcrumbs
        handleLoading={handleBodyAreaLoading}
        name={"About the Assessment"}
      />
      {!loadingStarted && (
        <div className="FSUnmask">
          <GridContainer>
            <GridItem
              lg={12}
              md={12}
              sm={12}
              xs={12}
              sx={{ marginTop: "30px" }}
            >
              <Box>
                <Typography sx={styles.learnMoreHeading}>
                  About the Assessment
                </Typography>
              </Box>
              <Box sx={{ marginTop: "30px" }}>
                <Typography sx={styles.bodyAreasText}>
                  The Abnormal Involuntary Movement Scale (AIMS) is a tool for
                  assessing and monitoring patients at risk of TD. Along with an
                  initial screening and differential diagnosis, an AIMS
                  assessment is an important step for appropriate management of
                  TD.
                </Typography>
              </Box>
              <Box sx={{ marginTop: "20px" }}>
                <img
                  src={AboutAssessmentOne}
                  alt="AboutAssessmentOne"
                  width="100%"
                  height="73px"
                />
              </Box>
              <Box sx={{ marginTop: "15px" }}>
                <Typography sx={styles.aboutAssessmentHeading}>
                  AIMS Consists of Two Parallel Processes
                </Typography>
              </Box>
              <Box sx={{ marginTop: "15px" }}>
                <Typography sx={styles.aboutAssessmentSubHeading}>
                  Observe and Ask
                </Typography>
                <Typography sx={styles.globalJudgmentsText}>
                  The first is an examination procedure that provides
                  instructions to systematically{" "}
                  <b>observe and ask about movements</b> in patients taking
                  APDs.
                </Typography>
              </Box>
              <Box sx={{ marginTop: "15px" }}>
                <Typography sx={styles.aboutAssessmentScoring}>
                  Scoring
                </Typography>
                <Typography sx={styles.globalJudgmentsText}>
                  The second process is scoring the severity of observed
                  movements on a 12-item scoring card.
                </Typography>
                <br />
                <Typography sx={styles.globalJudgmentsText}>
                  The AIMS assessment is composed of 12 items in 3 main
                  categories, 10 of which require a score from 0–4.
                  <sup>1,2</sup> Your score should be a reflection of your own
                  observations during the exam.
                </Typography>
                <br />
                <Typography sx={styles.globalJudgmentsText}>
                  0–4 points can be given to each area based on movement
                  severity <sup>2</sup>:
                </Typography>
                <ul>
                  <li>
                    <Typography sx={styles.globalJudgmentsText}>
                      0 = None
                    </Typography>
                  </li>
                  <li>
                    <Typography sx={styles.globalJudgmentsText}>
                      1 = Minimal (may be extreme normal)
                    </Typography>
                  </li>
                  <li>
                    <Typography sx={styles.globalJudgmentsText}>
                      2 = Mild
                    </Typography>
                  </li>
                  <li>
                    <Typography sx={styles.globalJudgmentsText}>
                      3 = Moderate
                    </Typography>
                  </li>
                  <li>
                    <Typography sx={styles.globalJudgmentsText}>
                      4 = Severe
                    </Typography>
                  </li>
                </ul>
                <Typography sx={styles.globalJudgmentsText}>
                  When providing a score, consider quality, frequency, and
                  amplitude of each movement <sup>1</sup>.
                </Typography>
                <br />
                <Typography sx={styles.globalJudgmentsText}>
                  <b>7 body areas</b> are assessed within 3 categories of
                  movements <sup>2</sup>.
                </Typography>
                <ul>
                  <li>
                    <Typography sx={styles.globalJudgmentsText}>
                      Facial and Oral
                    </Typography>
                  </li>
                  <li>
                    <Typography sx={styles.globalJudgmentsText}>
                      Extremity
                    </Typography>
                  </li>
                  <li>
                    <Typography sx={styles.globalJudgmentsText}>
                      Truncal
                    </Typography>
                  </li>
                </ul>
                <Typography sx={styles.globalJudgmentsText}>
                  <b>3 global judgment questions</b> are asked, these pertain to
                  patient capabilities and awareness of symptoms <sup>2</sup>.
                </Typography>
                <br />
                <Typography sx={styles.globalJudgmentsText}>
                  <b>2 dental status-related questions</b> evaluate dental
                  problems due to movements <sup>2</sup>.
                </Typography>
              </Box>
              <Box sx={{ marginTop: "20px" }}>
                <Typography sx={styles.aboutAssessmentHeading}>
                  When to Perform an AIMS Assessment
                </Typography>
                <br />
                <Typography sx={styles.boldTextStyle}>
                  The American Psychiatric Association (APA) recommends
                  performing a structured assessment:
                </Typography>
                <Box sx={{ display: "flex", marginTop: "20px" }}>
                  <img
                    src={AboutAssessmentTwo}
                    alt="AboutAssessmentTwo"
                    width="53"
                    height="51px"
                  />
                  <Box sx={{ marginTop: "4px", marginLeft: "10px" }}>
                    <Typography sx={styles.globalJudgmentsText}>
                      <b>Upon Initiation</b> of an ADP if abnormal involuntary
                      movements are detected during clinal assessment
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", marginTop: "20px" }}>
                  <img
                    src={AboutAssessmentThree}
                    alt="AboutAssessmentThree"
                    width="53"
                    height="51px"
                  />
                  <Box sx={{ marginTop: "14px", marginLeft: "10px" }}>
                    <Typography sx={styles.globalJudgmentsText}>
                      If a <b>new onset or exacerbation</b> of preexisting
                      movements is detected
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", marginTop: "20px" }}>
                  <img
                    src={AboutAssessmentFour}
                    alt="AboutAssessmentFour"
                    width="53"
                    height="51px"
                  />
                  <Box sx={{ marginLeft: "10px" }}>
                    <Typography sx={styles.globalJudgmentsText}>
                      A minimum of every 6 months in patients at high risk
                    </Typography>
                    <ul>
                      <li>
                        <Typography sx={styles.globalJudgmentsText}>
                          High cumulative exposure to APDs{" "}
                        </Typography>
                      </li>
                      <li>
                        <Typography sx={styles.globalJudgmentsText}>
                          {"Age > 55 years"}
                        </Typography>
                      </li>
                      <li>
                        <Typography
                          sx={styles.globalJudgmentsText}
                        ></Typography>
                        Mood Disorder
                      </li>
                      <li>
                        <Typography sx={styles.globalJudgmentsText}>
                          Other movement disorders, including drug-induced
                          Parkinonism
                        </Typography>
                      </li>
                    </ul>
                  </Box>
                </Box>
                <Box sx={{ display: "flex" }}>
                  <img
                    src={AboutAssessmentFive}
                    alt="AboutAssessmentFive"
                    width="53"
                    height="51px"
                  />
                  <Box sx={{ marginTop: "14px", marginLeft: "10px" }}>
                    <Typography sx={styles.globalJudgmentsText}>
                      At least <b>every 12 months</b> in other patients
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ marginTop: "20px" }}>
                <Typography sx={styles.aboutAssessmentHeading}>
                  Before You Begin the AIMS Assessment
                </Typography>
                <br />
                <Typography sx={styles.boldTextStyle}>STEP 1</Typography>
                <br />
                <Typography sx={styles.globalJudgmentsText}>
                  Ask the patient: <b>Is there anything in your mouth?</b>
                </Typography>
                <br />
                <Typography sx={styles.globalJudgmentsText}>
                  If yes, ask the patient to remove it. Gum chewing may resemble
                  orofacial movements which are among the most commonly observed
                  in TD.
                </Typography>
                <br />
                <Typography sx={styles.boldTextStyle}>STEP 2</Typography>
                <br />
                <Typography sx={styles.globalJudgmentsText}>
                  Ask the patient:
                  <b>
                    Do you wear dentures? Do your teeth or dentures bother you
                    now?
                  </b>
                </Typography>
                <br />
                <Typography sx={styles.globalJudgmentsText}>
                  Problems with teeth or dentures can lead to mistaken diagnosis
                  of dyskinesia.
                </Typography>
                <br />
                <Typography sx={styles.boldTextStyle}>STEP 3</Typography>
                <br />
                <Typography sx={styles.globalJudgmentsText}>
                  Ask the patient:
                  <b>
                    Do you notice any abnormal movements in your mouth, face,
                    hands, or feet?
                  </b>
                </Typography>
                <br />
                <Typography sx={styles.globalJudgmentsText}>
                  If yes, ask the patient to describe the movements and how
                  bothersome they are.
                </Typography>
                <br />
                <Typography sx={styles.globalJudgmentsText}>
                  Patients may be unaware of their movements or impact. Consider
                  seeking caregiver feedback about abnormal movements.
                </Typography>
              </Box>
              <Box sx={{ display: "flex", marginTop: "20px" }}>
                <Box
                  sx={{ width: "45%", borderBottom: "1px solid #9E9E9E" }}
                ></Box>
              </Box>
              <Box sx={{ marginTop: "20px" }}>
                <Typography sx={styles.globalJudgmentsText}>
                  References
                </Typography>
                <br />
                <Typography sx={styles.refrencesTextStyle}>
                  1. Munetz MR, Benjamin S. How to examine patients using the
                  Abnormal Involuntary Movement Scale. Hosp Community
                  Psychiatry. 1988;39:1172-1177.
                </Typography>
                <br />
                <Typography sx={styles.refrencesTextStyle}>
                  2. Guy W. ECDEU Assessment Manual for Psychopharmacology:
                  Revised. Rockville, MD: US Department of Health, Education and
                  Welfare, Public Health Service, Alcohol, Drug Abuse and Mental
                  Health Administration, NIMH Psychopharmacology Research
                  Branch, Division of Extramural Research Programs,
                  1976:534-537. DHEW publication number ADM 76-338
                </Typography>
              </Box>
            </GridItem>
          </GridContainer>
        </div>
      )}
    </Box>
  );
}

export default AboutAssessment;
